// packages/kb-view/src/kb-family.ts
//
// LAT-1832 (ADR 0058) — the `/kb/<team>/<kb>/<slug>` published-KB family.
//
// This module is the family's grammar AND its mount table. ADR 0058 §3
// requires a grammar separate from `docs-slug.ts`: it may import the shared
// alphabet, but it owns its own reserved sets and its own parser. Two
// families sharing one reserved list is how a change to one silently
// redefines the other.
//
// ADR 0058 §7: the apex carve-out and the SST zone routes (two patterns per
// mount, plus `/kb` and `/kb/*`) derive from the mount table below. Adding a
// mount without those registrations is a defect; deriving them here makes
// the omission unrepresentable.

import { isDocsSlug } from "./docs-slug.js";

/** The family's generic mount. Everything unmounted is addressed under it. */
export const KB_FAMILY_BASE_PATH = "/kb";

/**
 * ADR 0058 §2 — closed, operator-controlled. Not a customer feature. A mount
 * binds a path AND a renderer; the Guide stays at `/docs` with slug `guide`.
 */
export type PublishedKbRenderer = "docs" | "blog";

export interface PublishedKbMount {
  readonly teamSlug: string;
  readonly kbSlug: string;
  readonly mount: string;
  readonly renderer: PublishedKbRenderer;
}

export const PUBLISHED_KB_MOUNTS: readonly PublishedKbMount[] = [
  {
    teamSlug: "atmina-system",
    kbSlug: "guide",
    mount: "/docs",
    renderer: "docs",
  },
  {
    teamSlug: "atmina-system",
    kbSlug: "blog",
    mount: "/blog",
    renderer: "blog",
  },
];

/**
 * ADR 0058 §4 — reserved at the `<slug>` position so no published page can
 * shadow a fixed route. Own list, not `RESERVED_DOCS_SLUGS` (no `agents`).
 */
export const RESERVED_KB_FAMILY_SLUGS: readonly string[] = [
  "assets",
  "llms.txt",
  "llms-full.txt",
  "sitemap.xml",
  "robots.txt",
  "index",
  "search",
  "mcp.html",
];

/** Customer-published pages live under `wiki/<slug>.md`. The Guide stays `help/`. */
export const PUBLISHED_KB_PAGE_PREFIX = "wiki/";

export const PUBLISHED_KB_NAV_MANIFEST_PATH = "wiki/nav.yaml";

export const PUBLISHED_KB_RAW_MARKDOWN_SUFFIX = ".md";

/**
 * LAT-1916 — the wiki-root README is a KB's HOME PAGE (LAT-1884 ruling:
 * `wiki/README.md`, else the first Menu entry; the memory-setup seed writes it and lists
 * it first under "Start here"; CodeWiki names `README.md` as the one
 * case-sensitive filename). Its name is outside the page-slug alphabet on
 * purpose: it is never addressed as `/kb/<team>/<kb>/<slug>`, only as the
 * family's home route `/kb/<team>/<kb>` (or a mount's bare path). Nested hub
 * A nested README is a folder landing page and uses its containing folder's
 * public address. Only this root README maps to the family home route.
 */
export const PUBLISHED_KB_HOME_PATH = "wiki/README.md";

/**
 * The home page's catalog slug. Empty on purpose: §3 requires a non-empty
 * slug for every page address, so this value can never be requested as a page,
 * never collide with a real slug (`wiki/readme.md` → `readme` stays a page),
 * and sorts first in the derived, unlabelled menu.
 */
export const PUBLISHED_KB_HOME_SLUG = "";

export function isPublishedKbHomePath(path: string): boolean {
  return classifyPublishedKbFile(path).outcome === "home";
}

/** The address of a catalog slug under a base path: the home slug is the base path itself. */
export function publishedKbHrefForSlug(basePath: string, slug: string): string {
  return slug === PUBLISHED_KB_HOME_SLUG ? basePath : `${basePath}/${slug}`;
}

/** Same alphabet as ADR 0049 §1, imported rather than restated. */
export function isKbFamilySlug(slug: string): boolean {
  return isDocsSlug(slug);
}

export function isReservedKbFamilySlug(slug: string): boolean {
  return RESERVED_KB_FAMILY_SLUGS.includes(slug);
}

function isKbFamilyPagePath(path: string): boolean {
  const segments = path.split("/");
  return (
    segments.length > 0 &&
    segments.every(
      (segment) => isKbFamilySlug(segment) && !isReservedKbFamilySlug(segment),
    )
  );
}

export function mountForRef(
  teamSlug: string,
  kbSlug: string,
): PublishedKbMount | null {
  return (
    PUBLISHED_KB_MOUNTS.find(
      (entry) => entry.teamSlug === teamSlug && entry.kbSlug === kbSlug,
    ) ?? null
  );
}

export type PublishedKbPreviewContract =
  | {
      readonly kind: "generic-wiki";
      readonly basePath: string;
      readonly filePrefix: typeof PUBLISHED_KB_PAGE_PREFIX;
    }
  | {
      readonly kind: "mounted-renderer";
      readonly basePath: string;
      readonly renderer: PublishedKbRenderer;
    };

/**
 * Selects the public address and the renderer contract as one decision.
 *
 * The generic family is the only renderer whose `wiki/` grammar this package
 * can preview. Every named mount fails closed until its own renderer exposes a
 * matching preview contract; adding a future mount cannot silently inherit the
 * generic file rules.
 */
export function publishedKbPreviewContractForRef(
  teamSlug: string,
  kbSlug: string,
): PublishedKbPreviewContract {
  const mount = mountForRef(teamSlug, kbSlug);
  if (mount) {
    return {
      kind: "mounted-renderer",
      basePath: mount.mount,
      renderer: mount.renderer,
    };
  }
  return {
    kind: "generic-wiki",
    basePath: `${KB_FAMILY_BASE_PATH}/${teamSlug}/${kbSlug}`,
    filePrefix: PUBLISHED_KB_PAGE_PREFIX,
  };
}

/** The canonical root address for a published KB, including named mounts. */
export function publishedKbBasePathForRef(
  teamSlug: string,
  kbSlug: string,
): string {
  return publishedKbPreviewContractForRef(teamSlug, kbSlug).basePath;
}

export function mountByPath(pathname: string): PublishedKbMount | null {
  return (
    PUBLISHED_KB_MOUNTS.find(
      (entry) =>
        pathname === entry.mount || pathname.startsWith(`${entry.mount}/`),
    ) ?? null
  );
}

/**
 * Prefixes the apex catch-all must hard-404. Includes `/kb` and every mount
 * (so `/docs` and `/blog` cannot fall through to the marketing homepage).
 */
export function apexHard404Prefixes(): readonly string[] {
  return [KB_FAMILY_BASE_PATH, ...PUBLISHED_KB_MOUNTS.map((entry) => entry.mount)];
}

export function isApexHard404Path(pathname: string): boolean {
  return apexHard404Prefixes().some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Paths THIS family's parser owns: `/kb/*` and non-`/docs` mounts. `/docs`
 * stays with the ADR 0049 grammar — this module does not parse it.
 */
export function isKbFamilyHandledPath(pathname: string): boolean {
  if (
    pathname === KB_FAMILY_BASE_PATH ||
    pathname.startsWith(`${KB_FAMILY_BASE_PATH}/`)
  ) {
    return true;
  }
  return PUBLISHED_KB_MOUNTS.some(
    (entry) =>
      entry.mount !== "/docs" &&
      (pathname === entry.mount || pathname.startsWith(`${entry.mount}/`)),
  );
}

/**
 * SST zone-route patterns. Two per mount (bare + `/*`) plus two for `/kb`,
 * because a Cloudflare route pattern is a literal prefix match.
 *
 * `/docs` keeps the Pulumi names `DocsRoute` / `DocsBareRoute` so a derived
 * loop cannot replace the live v0.199+ resources.
 */
export interface WorkersRouteSpec {
  readonly resourceName: string;
  readonly pattern: string;
}

function mountResourceLabel(mount: string): string {
  if (mount === "/docs") return "Docs";
  const name = mount.slice(1);
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function workersRouteSpecs(host: string): readonly WorkersRouteSpec[] {
  const family: WorkersRouteSpec[] = [
    { resourceName: "KbFamilyBareRoute", pattern: `${host}/kb` },
    { resourceName: "KbFamilyRoute", pattern: `${host}/kb/*` },
  ];
  const mounts: WorkersRouteSpec[] = PUBLISHED_KB_MOUNTS.flatMap((entry) => {
    const label = mountResourceLabel(entry.mount);
    return [
      { resourceName: `${label}BareRoute`, pattern: `${host}${entry.mount}` },
      { resourceName: `${label}Route`, pattern: `${host}${entry.mount}/*` },
    ];
  });
  return [...family, ...mounts];
}

export function slugForPublishedKbPath(path: string): string | null {
  const classification = classifyPublishedKbFile(path);
  return classification.outcome === "page" ? classification.slug : null;
}

/**
 * LAT-2088 (RULING R63) — the media types a public rendition can actually be
 * BUILT from, defined here ONCE for every consumer of the published grammar.
 *
 * This is a mirror of the transform provider's documented input set
 * (`apps/mcp/src/adapters/images/cloudflare-images-rendition.ts`,
 * `IMAGES_INPUT_CONTENT_TYPES`). It cannot be imported from there — this
 * package is consumed by the credential-free docs worker and by the browser,
 * neither of which may reach into the API's adapters — so the copy is held
 * honest by a PARITY TEST that imports both and asserts set equality
 * (`apps/mcp/test/adapters/cloudflare-images-rendition.test.ts`). Widening the
 * adapter without widening this reddens there, which is the whole point:
 * before R63 the Sharing panel promised "served as a resized copy" for file
 * types the platform answers 503 for.
 *
 * The aliases (`image/jpg`, `image/heif`) are the provider's, kept verbatim so
 * the parity assertion is equality and not a subset.
 */
export const PUBLIC_RENDITION_SOURCE_MIME_TYPES: ReadonlySet<string> = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "image/heic",
  "image/heif",
]);

/**
 * The ratified Asset types that have NO derivative on today's provider.
 *
 * Exactly `ASSET_MIME_TYPES` minus the set above (the same parity test asserts
 * that arithmetic, so an Asset type added upstream lands in one list or the
 * other rather than silently in neither). They are still Assets — stored,
 * catalogued, addressable — so the public route asks upstream for them and
 * relays the honest 503 rather than pretending they are absent. What changes is
 * the PROMISE: Sharing says "not served yet", never "served as a resized copy".
 */
export const PUBLIC_UNRENDERABLE_ASSET_MIME_TYPES: ReadonlySet<string> = new Set([
  "image/bmp",
  "image/tiff",
  "application/pdf",
  "video/mp4", // Private playback only; public video is not admitted.
]);

/** `image/JPEG; charset=binary` and `image/jpeg` are one type, spelled twice. */
function normalizeMimeType(mimeType: string | null | undefined): string | null {
  return mimeType?.split(";", 1)[0]?.trim().toLowerCase() || null;
}

/**
 * Is this catalog row an Asset the public route will ASK UPSTREAM about?
 *
 * Both lists, deliberately: an unrenderable Asset must reach upstream so its
 * refusal is the transport truth (503 `rendition_unavailable`) rather than a
 * fabricated absence. Anything outside both lists is not an Asset at all and
 * takes the page grammar's answer.
 */
export function isPublicAssetMimeType(mimeType: string | null | undefined): boolean {
  const mime = normalizeMimeType(mimeType);
  return (
    mime !== null &&
    (PUBLIC_RENDITION_SOURCE_MIME_TYPES.has(mime) ||
      PUBLIC_UNRENDERABLE_ASSET_MIME_TYPES.has(mime))
  );
}

/** Public page addresses claimed by multiple distinct catalog paths. */
export function conflictingPublishedKbSlugs(
  paths: readonly string[],
): ReadonlySet<string> {
  const firstPathBySlug = new Map<string, string>();
  const conflicts = new Set<string>();
  for (const path of paths) {
    const slug = slugForPublishedKbPath(path);
    if (slug === null) continue;
    const firstPath = firstPathBySlug.get(slug);
    if (firstPath === undefined) firstPathBySlug.set(slug, path);
    else if (firstPath !== path) conflicts.add(slug);
  }
  return conflicts;
}

export type PublishedKbFileClassification =
  | { outcome: "home"; reason: "wiki-root-readme"; slug: "" }
  | {
      outcome: "page";
      reason: "flat-markdown-page" | "nested-markdown-page" | "folder-readme";
      slug: string;
    }
  | { outcome: "menu"; reason: "wiki-menu-manifest" }
  | { outcome: "asset"; reason: "served-as-rendition" }
  | { outcome: "asset-unsupported"; reason: "no-rendition-format" }
  | {
      outcome: "not-served";
      reason:
        | "outside-wiki"
        | "reserved-page-slug"
        | "invalid-page-slug"
        | "unsupported-file-shape";
    };

/**
 * The one current-grammar servability decision for every KB file.
 *
 * Both the public family and its in-app publication preview consume this
 * result. Keep the reason codes stable: they make an unserved file explainable
 * without giving either consumer a second copy of the grammar.
 *
 * LAT-2088 (ADR 0058 amendment 2026-09-08) — `mimeType` is OPTIONAL and, when
 * given, is asked FIRST, because an Asset is decided by what the bytes are and
 * not by where the file sits. That is what reclassifies an Asset path from
 * LAT-1966's "not served" to "served as rendition". A caller that does not know
 * the media type gets exactly the pre-LAT-2088 answer, so the page grammar and
 * LAT-1966's negatives are unchanged for every path-only caller.
 *
 * RULING R63 — the media question has THREE answers, not two. `asset` is a
 * PROMISE that a resized copy exists, so it is made only for the types the
 * transform provider reads; a BMP, a TIFF or a PDF is a real Asset with no
 * derivative today and gets its own outcome instead of a promise the public
 * route would answer 503 to. Anything outside both lists was never an Asset and
 * falls through to the page rules unchanged.
 *
 * A Companion page (`<asset path>.md`, ADR 0064 §2) is `text/markdown` and
 * therefore falls straight through to the page rules — no branch, no special
 * case. It publishes as an ordinary page when its path is a page path, with its
 * "(generated)" section label intact, exactly as the amendment requires.
 */
export function classifyPublishedKbFile(
  path: string,
  mimeType?: string | null,
): PublishedKbFileClassification {
  const mime = normalizeMimeType(mimeType);
  if (mime !== null && PUBLIC_RENDITION_SOURCE_MIME_TYPES.has(mime)) {
    return { outcome: "asset", reason: "served-as-rendition" };
  }
  if (mime !== null && PUBLIC_UNRENDERABLE_ASSET_MIME_TYPES.has(mime)) {
    return { outcome: "asset-unsupported", reason: "no-rendition-format" };
  }
  if (path === PUBLISHED_KB_HOME_PATH) {
    return { outcome: "home", reason: "wiki-root-readme", slug: "" };
  }
  if (path === PUBLISHED_KB_NAV_MANIFEST_PATH) {
    return { outcome: "menu", reason: "wiki-menu-manifest" };
  }
  if (!path.startsWith(PUBLISHED_KB_PAGE_PREFIX)) {
    return { outcome: "not-served", reason: "outside-wiki" };
  }

  const relativePath = path.slice(PUBLISHED_KB_PAGE_PREFIX.length);
  if (!relativePath.endsWith(PUBLISHED_KB_RAW_MARKDOWN_SUFFIX)) {
    return { outcome: "not-served", reason: "unsupported-file-shape" };
  }

  const markdownPath = relativePath.slice(
    0,
    -PUBLISHED_KB_RAW_MARKDOWN_SUFFIX.length,
  );
  const pathSegments = markdownPath.split("/");
  const isFolderReadme = pathSegments.length > 1 && pathSegments.at(-1) === "README";
  const slugSegments = isFolderReadme ? pathSegments.slice(0, -1) : pathSegments;

  if (slugSegments.some(isReservedKbFamilySlug)) {
    return { outcome: "not-served", reason: "reserved-page-slug" };
  }
  if (slugSegments.length === 0 || slugSegments.some((segment) => !isKbFamilySlug(segment))) {
    return { outcome: "not-served", reason: "invalid-page-slug" };
  }
  const slug = slugSegments.join("/");
  const reason = isFolderReadme
    ? "folder-readme"
    : slugSegments.length > 1
      ? "nested-markdown-page"
      : "flat-markdown-page";
  return { outcome: "page", reason, slug };
}

export function kbFamilyPathForSlug(
  teamSlug: string,
  kbSlug: string,
  slug: string,
): string {
  return `${KB_FAMILY_BASE_PATH}/${teamSlug}/${kbSlug}/${slug}`;
}

export type KbFamilyRoute =
  | { kind: "mcp"; teamSlug: string; kbSlug: string; basePath: string }
  | { kind: "not-found" }
  | { kind: "llms"; teamSlug: string; kbSlug: string; basePath: string }
  | { kind: "llms-full"; teamSlug: string; kbSlug: string; basePath: string }
  | { kind: "sitemap"; teamSlug: string; kbSlug: string; basePath: string }
  | { kind: "home-raw"; teamSlug: string; kbSlug: string; basePath: string }
  | {
      kind: "asset";
      teamSlug: string;
      kbSlug: string;
      assetPath: string;
      rendition: "thumb" | "medium";
      basePath: string;
    }
  | { kind: "mount-discovery"; mount: PublishedKbMount; filename: string }
  | {
      kind: "page";
      teamSlug: string;
      kbSlug: string;
      slug: string;
      basePath: string;
    }
  | {
      kind: "home";
      teamSlug: string;
      kbSlug: string;
      basePath: string;
    }
  | {
      kind: "raw";
      teamSlug: string;
      kbSlug: string;
      slug: string;
      basePath: string;
    }
  | {
      kind: "mount-home";
      mount: PublishedKbMount;
    }
  | {
      kind: "mount-page";
      mount: PublishedKbMount;
      slug: string;
    }
  | {
      kind: "mount-raw";
      mount: PublishedKbMount;
      slug: string;
    };

export type KbFamilyParse =
  | { kind: "route"; route: KbFamilyRoute; canonicalPath: string }
  | { kind: "redirect"; canonicalPath: string };

/**
 * ADR 0058 §5 — one canonical spelling per address.
 *
 * Page addresses lowercase whole. An ASSET address cannot: everything after
 * `assets/` is a CATALOG path, and the catalog is case-sensitive, so
 * lowercasing it would address a different file (or nothing). Only the family's
 * own segments — `kb`, `<team>`, `<kb>`, and the reserved `assets` name itself —
 * are case-folded, and the asset path is carried through byte for byte.
 */
function canonicalizeFamilyPath(pathname: string): string | null {
  if (!isKbFamilyHandledPath(pathname)) return null;
  const trimmed = pathname.replace(/\/+$/, "") || pathname;
  const segments = trimmed.split("/");
  if (
    segments[1]?.toLowerCase() === "kb" &&
    segments[4]?.toLowerCase() === "assets"
  ) {
    return [
      "",
      "kb",
      segments[2]?.toLowerCase(),
      segments[3]?.toLowerCase(),
      "assets",
      ...segments.slice(5),
    ].join("/");
  }
  return trimmed.toLowerCase();
}

/**
 * Turn the URL tail after `assets/` into the catalog path it addresses, or
 * `null` when it addresses nothing this family will look up.
 *
 * One percent-decode per segment, exactly like `./kb-link-target` — never a
 * whole-path decode, because a decoded `%2F` must not become a separator after
 * the split. A segment that decodes to `.`, `..`, a separator, a backslash or a
 * control character is refused rather than clamped: the caller hands this
 * straight to a catalog lookup, so a path that could climb, traverse or smuggle
 * a header must not exist as a value at all.
 *
 * `null` is not an error the surface may narrate — it takes the family's one
 * not-found projection (§6) like every other absence.
 */
export function decodePublishedAssetPath(path: string): string | null {
  if (path === "") return null;
  const decoded: string[] = [];
  for (const segment of path.split("/")) {
    if (segment === "") return null;
    let value: string;
    try {
      value = decodeURIComponent(segment);
    } catch {
      return null;
    }
    if (
      value === "." ||
      value === ".." ||
      value.includes("/") ||
      value.includes("\\") ||
      Array.from(value).some((character) => {
        const code = character.charCodeAt(0);
        return code <= 0x1f || code === 0x7f;
      })
    ) {
      return null;
    }
    decoded.push(value);
  }
  return decoded.join("/");
}

/** The two renditions the public origin serves, and the one it defaults to. */
export type PublicAssetRendition = "thumb" | "medium";

/**
 * The Asset route's query policy: the family's ONE parameterised address.
 *
 * `rendition` selects `thumb` or `medium` and DEFAULTS to `medium`. Everything
 * else is `null`, which every caller turns into the family's one not-found
 * projection (§6) rather than an error that would separate the causes:
 *
 *   * `rendition=original` — never served on the public origin, and it must be
 *     indistinguishable from a missing Asset. A 400 would confirm the URL names
 *     something real.
 *   * any other parameter, or a repeated `rendition` — the surface reads one
 *     parameter and one only, so an unrecognised spelling is not "close
 *     enough"; collapsing them would let a caller mint distinct addresses for
 *     one object.
 *
 * LAT-2088 review Minor 17 — this used to be ~15 duplicated lines here and in
 * `apps/docs/src/grammar.ts`, in a module whose own header calls two copies of
 * a published grammar "the silent-divergence failure mode §1 exists to
 * prevent". `apps/docs` already imports this module (`decodePublishedAssetPath`
 * above), and nothing in this package imports `apps/docs`, so there is no cycle
 * to pay for: one definition, both grammars.
 */
export function parsePublicAssetRenditionQuery(
  url: URL,
): PublicAssetRendition | null {
  const keys = [...url.searchParams.keys()];
  if (keys.some((key) => key !== "rendition")) return null;
  if (url.searchParams.getAll("rendition").length > 1) return null;
  const rendition = url.searchParams.get("rendition");
  if (rendition === null) return "medium";
  return rendition === "thumb" || rendition === "medium" ? rendition : null;
}

function routeGeneric(
  teamSlug: string,
  kbSlug: string,
  rest: string,
): KbFamilyRoute {
  const mount = mountForRef(teamSlug, kbSlug);
  const basePath = mount
    ? mount.mount
    : `${KB_FAMILY_BASE_PATH}/${teamSlug}/${kbSlug}`;

  if (rest === "") {
    return mount
      ? { kind: "mount-home", mount }
      : { kind: "home", teamSlug, kbSlug, basePath };
  }

  if (rest.startsWith("assets/")) {
    const assetPath = decodePublishedAssetPath(rest.slice("assets/".length));
    if (assetPath === null || mount) return { kind: "not-found" };
    return {
      kind: "asset",
      teamSlug,
      kbSlug,
      assetPath,
      rendition: "medium",
      basePath,
    };
  }

  const fixed = rest === "mcp.html" ? "mcp"
    : rest === "llms.txt" ? "llms"
    : rest === "llms-full.txt" ? "llms-full"
    : rest === "sitemap.xml" ? "sitemap"
    : rest === "index.md" ? "home-raw"
    : null;
  if (fixed !== null) {
    if (mount) return mount.renderer === "docs" && fixed !== "home-raw"
      ? { kind: "mount-discovery", mount, filename: rest }
      : { kind: "not-found" };
    return { kind: fixed, teamSlug, kbSlug, basePath };
  }

  if (rest.endsWith(PUBLISHED_KB_RAW_MARKDOWN_SUFFIX)) {
    const slug = rest.slice(0, -PUBLISHED_KB_RAW_MARKDOWN_SUFFIX.length);
    if (!isKbFamilyPagePath(slug)) {
      return { kind: "not-found" };
    }
    return mount
      ? { kind: "mount-raw", mount, slug }
      : { kind: "raw", teamSlug, kbSlug, slug, basePath };
  }

  if (!isKbFamilyPagePath(rest)) {
    return { kind: "not-found" };
  }
  return mount
    ? { kind: "mount-page", mount, slug: rest }
    : { kind: "page", teamSlug, kbSlug, slug: rest, basePath };
}

function routeForCanonicalPath(canonicalPath: string): KbFamilyRoute {
  const mount = mountByPath(canonicalPath);
  if (mount && mount.mount !== "/docs") {
    if (canonicalPath === mount.mount) return { kind: "mount-home", mount };
    const rest = canonicalPath.slice(mount.mount.length + 1);
    return routeGeneric(mount.teamSlug, mount.kbSlug, rest);
  }

  if (canonicalPath === KB_FAMILY_BASE_PATH) return { kind: "not-found" };

  const rest = canonicalPath.slice(KB_FAMILY_BASE_PATH.length + 1);
  const segments = rest.split("/");
  if (segments.length < 2) return { kind: "not-found" };
  const [teamSlug, kbSlug] = segments;
  if (!teamSlug || !kbSlug) return { kind: "not-found" };
  if (!isKbFamilySlug(teamSlug) || !isKbFamilySlug(kbSlug)) {
    return { kind: "not-found" };
  }
  // LAT-2088 — everything past `<team>/<kb>` is handed to `routeGeneric` WHOLE
  // rather than capped at one segment, because an asset address carries a
  // nested catalog path. The cap has not moved for pages: `routeGeneric` still
  // refuses any remainder containing a separator once the `assets/` sibling has
  // had its say, so `/kb/<team>/<kb>/a/b` is the same not-found it always was.
  return routeGeneric(teamSlug, kbSlug, segments.slice(2).join("/"));
}

function canonicalPathForRoute(route: KbFamilyRoute): string | null {
  switch (route.kind) {
    case "not-found":
      return null;
    case "home":
      return route.basePath;
    // An asset address is already canonical when it parses (see `routeGeneric`
    // and `parseKbFamilyRequest`): it is never a redirect TARGET, so it reports
    // no mount rewrite here.
    case "asset":
      return null;
    case "home-raw":
      return `${route.basePath}/index.md`;
    case "llms":
      return `${route.basePath}/llms.txt`;
    case "mcp":
      return `${route.basePath}/mcp.html`;
    case "llms-full":
      return `${route.basePath}/llms-full.txt`;
    case "sitemap":
      return `${route.basePath}/sitemap.xml`;
    case "mount-discovery":
      return `${route.mount.mount}/${route.filename}`;
    case "page":
      return `${route.basePath}/${route.slug}`;
    case "raw":
      return `${route.basePath}/${route.slug}${PUBLISHED_KB_RAW_MARKDOWN_SUFFIX}`;
    case "mount-home":
      return route.mount.mount;
    case "mount-page":
      return `${route.mount.mount}/${route.slug}`;
    case "mount-raw":
      return `${route.mount.mount}/${route.slug}${PUBLISHED_KB_RAW_MARKDOWN_SUFFIX}`;
  }
}

/**
 * Parse one request URL against the `/kb` family grammar (and non-docs mounts).
 *
 * A non-canonical spelling of a routable address redirects; a non-canonical
 * spelling of a malformed one does not. Mounted KBs 301 from the generic
 * address to the mount in one hop, including when the generic spelling was
 * non-canonical.
 */
export function parseKbFamilyRequest(url: URL): KbFamilyParse {
  const canonicalPath = canonicalizeFamilyPath(url.pathname);
  if (canonicalPath === null) {
    return {
      kind: "route",
      route: { kind: "not-found" },
      canonicalPath: url.pathname,
    };
  }

  const route = routeForCanonicalPath(canonicalPath);
  const mountTarget = canonicalPathForRoute(route);
  const spellingDiffers = canonicalPath !== url.pathname;
  const carriesQuery = url.search !== "";

  // The Asset route's query policy is `parsePublicAssetRenditionQuery` above —
  // ONE definition, shared with `apps/docs/src/grammar.ts`. Its refusals and
  // this one both take the §6 not-found projection:
  //
  //   * a refused query (`rendition=original`, an unknown rendition, a repeated
  //     or foreign parameter) — see that function for why each is a refusal
  //     rather than an error;
  //   * a non-canonical spelling — a page 301s, an asset cannot (see
  //     `routeGeneric`: ADR 0049 §3's redirect seam takes no catalog path), so
  //     it fails closed instead of minting a `Location` from request bytes.
  if (route.kind === "asset") {
    const rendition = parsePublicAssetRenditionQuery(url);
    if (rendition === null || spellingDiffers) {
      return { kind: "route", route: { kind: "not-found" }, canonicalPath };
    }
    return { kind: "route", route: { ...route, rendition }, canonicalPath };
  }

  if (route.kind !== "not-found" && mountTarget !== null) {
    const needsRedirect =
      spellingDiffers || carriesQuery || mountTarget !== canonicalPath;
    if (needsRedirect) {
      return { kind: "redirect", canonicalPath: mountTarget };
    }
  }

  return { kind: "route", route, canonicalPath };
}
